import { useState, useEffect, useCallback } from "react";
import {
  Pressable,
  ActivityIndicator,
  ScrollView,
  View,
  Text,
} from "react-native";
import { useRouter, useFocusEffect } from "expo-router";
import { consumePick } from "@/lib/pickerMailbox";
import { useConfirm } from "@/hooks/useConfirm";
import { useAlert } from "@/hooks/useAlert";
import ConfirmSheet from "@/components/ConfirmSheet";
import AlertSheet from "@/components/AlertSheet";
import { doc, updateDoc, setDoc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius, font, spacing } from "@/lib/theme";
import { teamColor } from "@/lib/teamColor";
import ContentWidth from "@/components/ContentWidth";
import styled from "styled-components/native";

interface TeamConfig {
  name: string;
  priority: number;
  captainId: string;
  captainName: string;
  viceCaptainId: string;
  viceCaptainName: string;
}

const PICKER_KEY = "teams-screen";

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function emptyTeam(name = "", priority = 1): TeamConfig {
  return { name, priority, captainId: "", captainName: "", viceCaptainId: "", viceCaptainName: "" };
}

function initials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const CardShadow = styled.View`
  margin: 8px 20px 18px;
  border-radius: ${radius.lg}px;
  background-color: ${colors.white};
  shadow-color: #000;
  shadow-offset: 0px 4px;
  shadow-opacity: 0.12;
  shadow-radius: 12px;
  elevation: 4;
`;

const Card = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const HeaderRow = styled.View<{ bg: string; border: string }>`
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  background-color: ${(p) => p.bg};
  border-bottom-width: 1px;
  border-bottom-color: ${(p) => p.border};
`;

const TeamDot = styled.View<{ bg: string }>`
  width: 14px;
  height: 14px;
  border-radius: 7px;
  background-color: ${(p) => p.bg};
  margin-right: 12px;
`;

const TeamName = styled.Text`
  font-size: 18px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  flex: 1;
`;

const MenuBtn = styled.Pressable`
  padding: 8px;
`;

const MenuDots = styled.Text`
  font-size: 22px;
  color: ${colors.inkMuted};
  font-weight: 700;
`;

const SubLabel = styled.Text`
  font-size: 11px;
  font-weight: 800;
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

const RoleSection = styled.View`
  padding: 16px;
  border-top-width: 1px;
  border-top-color: ${colors.line};
`;

const RoleRow = styled.Pressable`
  flex-direction: row;
  align-items: center;
`;

const Avatar = styled.View<{ bg: string; border: string }>`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${(p) => p.bg};
  border: 1.5px solid ${(p) => p.border};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text<{ color: string }>`
  font-size: 14px;
  font-weight: 800;
  color: ${(p) => p.color};
`;

const PersonName = styled.Text`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.ink};
  flex: 1;
`;

const PersonPlaceholder = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  font-style: italic;
  flex: 1;
`;

const ChangeLink = styled.Text`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.brand};
`;

const AddTeamCard = styled.Pressable`
  margin: 8px 20px 16px;
  background-color: transparent;
  border-radius: ${radius.lg}px;
  border: 2px dashed ${colors.line};
  padding: 20px;
  align-items: center;
  justify-content: center;
`;

const AddTeamText = styled.Text`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.brand};
`;

const SectionHead = styled.View`
  padding: 20px 20px 4px;
`;

const SectionLabelBig = styled.Text`
  font-size: 12px;
  font-weight: 800;
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RenameInput = styled.TextInput`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.ink};
  background-color: ${colors.bg};
  border-radius: ${radius.md}px;
  border: 1px solid ${colors.line};
  padding: 12px 16px;
  margin: 12px 20px;
`;

export default function TeamsScreen() {
  const { club } = useClub();
  const router = useRouter();

  const [teams, setTeams] = useState<TeamConfig[]>([]);
  const [originalNames, setOriginalNames] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<{ teamIndex: number; field: "captain" | "viceCaptain" } | null>(null);
  const [menuOpenIndex, setMenuOpenIndex] = useState<number | null>(null);
  const [renamingIndex, setRenamingIndex] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const { confirmProps, confirm } = useConfirm();
  const { alertProps, alert } = useAlert();

  useEffect(() => {
    if (!club) return;
    async function loadTeamConfigs() {
      const snap = await getDocs(collection(db, "clubs", club!.id, "teamConfig"));
      const configs = new Map<string, any>();
      snap.docs.forEach((d) => configs.set(d.data().name, d.data()));

      const existing = club!.teams || [];
      const loaded = existing.map((name: string, i: number) => {
        const saved = configs.get(name);
        if (saved) {
          return {
            name: saved.name || name,
            priority: saved.priority ?? i + 1,
            captainId: saved.captainId || "",
            captainName: saved.captainName || "",
            viceCaptainId: saved.viceCaptainId || "",
            viceCaptainName: saved.viceCaptainName || "",
          };
        }
        return emptyTeam(name, i + 1);
      });
      setTeams(loaded);
      setOriginalNames(existing);
    }
    loadTeamConfigs();
  }, [club?.id]);

  const handleAdd = () => {
    setTeams((prev) => [...prev, emptyTeam("New team", prev.length + 1)]);
    setRenamingIndex(teams.length);
    setRenameValue("New team");
  };

  const handleStartRename = (index: number) => {
    setMenuOpenIndex(null);
    setRenamingIndex(index);
    setRenameValue(teams[index].name);
  };

  const handleConfirmRename = () => {
    if (renamingIndex == null) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingIndex(null);
      return;
    }
    setTeams((prev) => {
      const next = [...prev];
      next[renamingIndex] = { ...next[renamingIndex], name: trimmed };
      return next;
    });
    setRenamingIndex(null);
  };

  const handleDelete = async (index: number) => {
    setMenuOpenIndex(null);
    const team = teams[index];
    const ok = await confirm({
      title: "Delete team",
      message: `Delete "${team.name}"? This can't be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (ok) setTeams((prev) => prev.filter((_, i) => i !== index));
  };

  const openPicker = (teamIndex: number, field: "captain" | "viceCaptain") => {
    setPickerTarget({ teamIndex, field });
    const team = teams[teamIndex];
    const selectedId = field === "captain" ? team.captainId : team.viceCaptainId;
    router.push(`/(app)/pick-captain?field=${field}&selectedId=${selectedId}&key=${PICKER_KEY}`);
  };

  useFocusEffect(
    useCallback(() => {
      const result = consumePick(PICKER_KEY);
      if (!result || !pickerTarget) return;
      const { teamIndex } = pickerTarget;
      setTeams((prev) => {
        const next = [...prev];
        if (result.field === "captain") {
          next[teamIndex] = { ...next[teamIndex], captainId: result.id, captainName: result.name };
        } else {
          next[teamIndex] = { ...next[teamIndex], viceCaptainId: result.id, viceCaptainName: result.name };
        }
        return next;
      });
      setPickerTarget(null);
    }, [pickerTarget])
  );

  const handleSave = async () => {
    if (!club) return;
    const cleaned = teams.filter((t) => t.name.trim());
    const names = cleaned.map((t) => t.name.trim());
    if (new Set(names).size !== names.length) {
      await alert({ title: "Duplicate name", message: "Each team must have a unique name." });
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "clubs", club.id), { teams: names });

      // Delete configs for removed teams
      const removed = originalNames.filter((n) => !names.includes(n));
      await Promise.all(removed.map((n) => deleteDoc(doc(db, "clubs", club.id, "teamConfig", slugify(n)))));

      // Write/update configs (priority assigned by current order)
      await Promise.all(
        cleaned.map((t, i) =>
          setDoc(doc(db, "clubs", club.id, "teamConfig", slugify(t.name)), {
            name: t.name.trim(),
            priority: i + 1,
            captainId: t.captainId,
            captainName: t.captainName,
            viceCaptainId: t.viceCaptainId,
            viceCaptainName: t.viceCaptainName,
          })
        )
      );

      router.back();
    } catch (err: any) {
      await alert({ title: "Error", message: err?.message || "Failed to save teams." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ScrollView style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
        <ContentWidth>
          <SectionHead>
            <SectionLabelBig>Teams</SectionLabelBig>
          </SectionHead>

          {teams.map((team, i) => {
            const c = teamColor(team.name, i);
            return (
              <CardShadow key={i}>
              <Card>
                <HeaderRow bg={c.bg} border={c.border}>
                  <TeamDot bg={c.dot} />
                  {renamingIndex === i ? (
                    <View style={{ flex: 1 }}>
                      <RenameInput
                        autoFocus
                        value={renameValue}
                        onChangeText={setRenameValue}
                        onBlur={handleConfirmRename}
                        onSubmitEditing={handleConfirmRename}
                        returnKeyType="done"
                        placeholder="Team name"
                        placeholderTextColor={colors.inkMuted}
                        style={{ margin: 0 }}
                      />
                    </View>
                  ) : (
                    <TeamName>{team.name}</TeamName>
                  )}
                  <MenuBtn onPress={() => setMenuOpenIndex(menuOpenIndex === i ? null : i)}>
                    <MenuDots>⋯</MenuDots>
                  </MenuBtn>
                </HeaderRow>

                {menuOpenIndex === i && (
                  <View style={{ borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.bg }}>
                    <Pressable onPress={() => handleStartRename(i)} style={({ pressed }) => ({ padding: 14, opacity: pressed ? 0.6 : 1 })}>
                      <Text style={{ fontSize: 14, fontWeight: "600", color: colors.ink }}>Rename</Text>
                    </Pressable>
                    <Pressable onPress={() => handleDelete(i)} style={({ pressed }) => ({ padding: 14, opacity: pressed ? 0.6 : 1, borderTopWidth: 1, borderTopColor: colors.line })}>
                      <Text style={{ fontSize: 14, fontWeight: "600", color: colors.danger }}>Delete team</Text>
                    </Pressable>
                  </View>
                )}

                <RoleSection>
                  <SubLabel>Captain</SubLabel>
                  <RoleRow onPress={() => openPicker(i, "captain")}>
                    <Avatar bg={team.captainName ? c.bg : colors.bg} border={team.captainName ? c.border : colors.line}>
                      <AvatarText color={team.captainName ? c.text : colors.inkMuted}>
                        {team.captainName ? initials(team.captainName) : "+"}
                      </AvatarText>
                    </Avatar>
                    {team.captainName ? <PersonName>{team.captainName}</PersonName> : <PersonPlaceholder>Tap to assign</PersonPlaceholder>}
                    <ChangeLink>{team.captainName ? "Change" : ""}</ChangeLink>
                  </RoleRow>
                </RoleSection>

                <RoleSection>
                  <SubLabel>Vice Captain</SubLabel>
                  <RoleRow onPress={() => openPicker(i, "viceCaptain")}>
                    <Avatar bg={team.viceCaptainName ? c.bg : colors.bg} border={team.viceCaptainName ? c.border : colors.line}>
                      <AvatarText color={team.viceCaptainName ? c.text : colors.inkMuted}>
                        {team.viceCaptainName ? initials(team.viceCaptainName) : "+"}
                      </AvatarText>
                    </Avatar>
                    {team.viceCaptainName ? <PersonName>{team.viceCaptainName}</PersonName> : <PersonPlaceholder>Tap to assign</PersonPlaceholder>}
                    <ChangeLink>{team.viceCaptainName ? "Change" : ""}</ChangeLink>
                  </RoleRow>
                </RoleSection>
              </Card>
              </CardShadow>
            );
          })}

          <AddTeamCard onPress={handleAdd}>
            <AddTeamText>+ Add team</AddTeamText>
          </AddTeamCard>

          <Pressable
            onPress={handleSave}
            disabled={saving}
            style={({ pressed }) => ({
              backgroundColor: colors.brand,
              borderRadius: radius.lg,
              padding: 16,
              alignItems: "center",
              marginHorizontal: 20,
              marginTop: 20,
              opacity: pressed ? 0.9 : 1,
            })}
          >
            {saving ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={{ color: "#fff", fontSize: 16, fontWeight: "700" }}>Save</Text>
            )}
          </Pressable>

          <View style={{ height: 40 }} />
        </ContentWidth>
      </ScrollView>

      <ConfirmSheet {...confirmProps} />
      <AlertSheet {...alertProps} />
    </>
  );
}
